import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { ChildTileData, CMSViewMode } from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';
import { getParentTileImage } from '../../../../utils/tileImageUtils';

interface CMSConsumerCollectionsGridProps {
  collectionsData: ChildTileData[];
  // Category id of the page these collections are shown under; selects that parent's tile image
  parentCategoryId?: string;
  applicationName?: string;
  marginTop?: number | string;
  marginBottom?: number | string;
  viewMode?: CMSViewMode;
  header?: string;
  showHeader?: boolean;
  gridSpacing?: number;
  imageHeight?: { xs: string; lg: string };
  headerType?: 'main' | 'application';
}

const CMSConsumerCollectionsGrid: React.FC<CMSConsumerCollectionsGridProps> = ({
                                                                                 collectionsData,
                                                                                 parentCategoryId,
                                                                                 applicationName,
                                                                                 marginTop = 5,
                                                                                 marginBottom = 5,
                                                                                 viewMode = 'live',
                                                                                 header,
                                                                                 showHeader = true,
                                                                                 gridSpacing = 5,
                                                                                 imageHeight = { xs: '150px', lg: '276px' },
                                                                                 headerType = 'main'
                                                                               }) => {
  const navigate = useNavigate();

  const handleCollectionClick = async (item: ChildTileData) => {
    try {
      const categoryResponse = await getCategoryDetails(item.category_id, viewMode);
      if (categoryResponse.success && categoryResponse.cms_category) {
        const categoryUrl = buildCategoryUrl(categoryResponse.cms_category);
        navigate(categoryUrl);
      } else {
        if (item.item_group && item.collection) {
          navigate(`/item_group/${item.item_group}/collection/${encodeURIComponent(item.collection)}`);
        } else if (item.application && item.collection) {
          navigate(`/item_group/${encodeURIComponent(item.application)}/collection/${encodeURIComponent(item.collection)}`);
        }
      }
    } catch (error) {
      console.error('Error navigating to collection:', error);
      if (item.item_group && item.collection) {
        navigate(`/item_group/${item.item_group}/collection/${encodeURIComponent(item.collection)}`);
      }
    }
  };

  const getHeaderText = () => {
    if (header) return header;
    if (applicationName) return `${applicationName} Collections`;
    return 'Collections';
  };

  const getHeaderStyles = () => {
    switch (headerType) {
      case 'main':
        return { fontSize: '56px', fontWeight: '400', mb: 4 };
      case 'application':
        return { fontSize: '32px', fontWeight: '400', mb: 4 };
      default:
        return { fontSize: '56px', fontWeight: '400', mb: 4 };
    }
  };

  const headerStyles = getHeaderStyles();

  if ((!collectionsData || collectionsData.length === 0) && !showHeader) {
    return <MissingContent sectionName="Collections" />;
  }

  if ((!collectionsData || collectionsData.length === 0) && showHeader) {
    return (
      <Box sx={{ mt: marginTop, mb: marginBottom, ml: { xs: '5%', lg: '5rem' }, mr: { xs: '5%', lg: '5rem' } }}>
        <Typography
          variant="h4"
          mb={headerStyles.mb}
          fontWeight={headerStyles.fontWeight}
          fontSize={headerStyles.fontSize}
          sx={{ textTransform: 'capitalize' }}
        >
          {getHeaderText()}
        </Typography>
      </Box>
    );
  }

  const publishedCollections = collectionsData
    .filter(item => item.is_published)
    .sort((a, b) => {
      const nameA = (a.collection || a.category_name || '').toLowerCase();
      const nameB = (b.collection || b.category_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  return (
    <Box sx={{ mt: marginTop, mb: marginBottom, ml: { xs: '5%', lg: '5rem' }, mr: { xs: '5%', lg: '5rem' } }}>
      {showHeader && (
        <Typography
          variant="h4"
          mb={headerStyles.mb}
          fontWeight={headerStyles.fontWeight}
          fontSize={headerStyles.fontSize}
          sx={{ textTransform: 'capitalize' }}
        >
          {getHeaderText()}
        </Typography>
      )}

      <Grid container columnSpacing={gridSpacing} rowSpacing={10}>
        {publishedCollections.map((item, index) => {
          const displayName = item.collection || item.category_name || 'Untitled Collection';
          const imageUrl = getParentTileImage(item, parentCategoryId) || '/placeholder-image.jpg';

          return (
            <Grid key={item.category_id || index} item xs={6} sm={3} md={2}>
              <Box
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  height: imageHeight,
                  width: '100%',
                  '&:hover img': { transform: 'scale(1.03)' },
                  overflow: 'hidden',
                }}
                onClick={() => handleCollectionClick(item)}
                onContextMenu={(e) => e.stopPropagation()}
              >
                <img
                  src={imageUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease',
                  }}
                  alt={displayName}
                />

                {/* Overlay Text */}
                {item.overlay_text && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      backgroundColor: item.overlay_text.background_colour_hex || '#000000',
                      px: 2,
                      py: 0.75,
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: `${item.overlay_text.text_size_px || 14}px`,
                        fontWeight: 600,
                        color: item.overlay_text.text_colour_hex || '#FFFFFF',
                      }}
                    >
                      {item.overlay_text.overlay_text}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', flexDirection: 'column' }}>
                <Typography
                  mt={1}
                  fontWeight={'600'}
                  textTransform={'capitalize'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCollectionClick(item)}
                >
                  {displayName}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CMSConsumerCollectionsGrid;